import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getAvailableClinicians,
  type AvailableClinician,
  type TaskListResponse,
} from '../services/api';
import {
  TASK_TYPE_DISCIPLINE_MAP,
  type TaskTypeId,
} from '../constants/visitTypes';
import {
  clinicianMatchesDiscipline,
  findDuplicateClinicianNames,
  getAssigneeDisciplineForTask,
  getClinicianDisplayName,
  type ClinicianSelectInput,
} from '../utils/clinicianUtils';

export interface UsePatientAssigneesParams {
  /** False until the patient has loaded; the roster is not fetched before. */
  patientLoaded: boolean;
  /** The patient's clinic. Without one, the roster is not clinic-filtered. */
  clinicId: string | null | undefined;
  showError: (title: string, message?: string) => void;
}

export interface UsePatientAssigneesResult {
  /** Everyone at the clinic, assignable clinician or not. */
  clinicians: AvailableClinician[];
  /** Only the staff who can be assigned a visit. */
  assignableClinicians: AvailableClinician[];
  getClinicAssignableClinicians: (
    clinicId: string | null | undefined,
    discipline?: (typeof TASK_TYPE_DISCIPLINE_MAP)[TaskTypeId],
    alwaysIncludeClinicianId?: string | null
  ) => AvailableClinician[];
  duplicateClinicianNames: Set<string>;
  duplicateAssignableClinicianNames: Set<string>;
  formatDuplicateClinicianOptionLabel: (
    clinician: ClinicianSelectInput,
    label: string
  ) => string;
  getTaskAssigneeLabel: (task: TaskListResponse) => string;
  getTaskAssigneeClinicians: (task: TaskListResponse) => ClinicianSelectInput[];
}

/**
 * Owns the patient's clinic roster and every task-assignee picker built from
 * it: who a task may be assigned to, the label for its current assignee, and
 * the email suffix that tells two people with the same name apart.
 *
 * Extracted from `PatientDetail` so these rules can be tested without
 * rendering the page. It reads only the roster and touches no other page
 * state.
 */
export function usePatientAssignees({
  patientLoaded,
  clinicId,
  showError,
}: UsePatientAssigneesParams): UsePatientAssigneesResult {
  const [clinicians, setClinicians] = useState<AvailableClinician[]>([]);

  useEffect(() => {
    if (!patientLoaded) return;
    // Clear immediately so stale data from a previous clinic doesn't bleed
    // through while the new fetch is in-flight.
    setClinicians([]);
    let cancelled = false;
    const fetchClinicians = async () => {
      try {
        // Everyone at the clinic: a visit needs an assignable clinician, but
        // any member may take an order, communication or misc task.
        const response = await getAvailableClinicians({
          query: {
            ...(clinicId ? { clinic_id: clinicId } : {}),
            include_all_members: true,
          },
        });
        if (!cancelled && response?.data) {
          setClinicians(response.data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching clinicians:', err);
          showError(
            'Load Error',
            'Unable to load clinicians. Please try again.'
          );
        }
      }
    };
    void fetchClinicians();
    return () => {
      cancelled = true;
    };
  }, [patientLoaded, clinicId, showError]);

  const assignableClinicians = useMemo(
    () => clinicians.filter(c => c.is_assignable_clinician),
    [clinicians]
  );

  const getClinicAssignableClinicians = useMemo(() => {
    const dedup = (
      list: typeof assignableClinicians,
      preserveId?: string | null
    ) => {
      const seen = new Set<string>();
      const result: typeof assignableClinicians = [];

      const preserved = preserveId
        ? list.find(c => c.id === preserveId)
        : undefined;
      if (preserved) {
        result.push(preserved);
        seen.add(preserved.user_id);
      }

      for (const c of list) {
        if (c.id === preserveId || seen.has(c.user_id)) continue;
        seen.add(c.user_id);
        result.push(c);
      }
      return result;
    };
    return (
      clinicId: string | null | undefined,
      discipline?: (typeof TASK_TYPE_DISCIPLINE_MAP)[TaskTypeId],
      alwaysIncludeClinicianId?: string | null
    ) => {
      // A visit needs an assignable clinician of its discipline. Any other
      // task (no discipline) may go to anyone at the clinic.
      const source = discipline ? assignableClinicians : clinicians;
      const filtered = source.filter(c => {
        if (alwaysIncludeClinicianId && c.id === alwaysIncludeClinicianId) {
          return true;
        }
        if (clinicId && c.clinic_id !== clinicId) return false;
        return clinicianMatchesDiscipline(c, discipline);
      });
      return dedup(filtered, alwaysIncludeClinicianId);
    };
  }, [assignableClinicians, clinicians]);

  const getAssignedClinicianName = useCallback(
    (task: TaskListResponse) => {
      if (!task.assigned_clinician_id) return 'Unassigned';

      if (task.assigned_clinician_name) {
        return task.assigned_clinician_name;
      }

      const clinician = getClinicAssignableClinicians(
        task.agency_clinic_id,
        getAssigneeDisciplineForTask(task),
        task.assigned_clinician_id
      ).find(c => c.id === task.assigned_clinician_id);

      return clinician
        ? getClinicianDisplayName(clinician)
        : 'Assigned clinician unavailable';
    },
    [getClinicAssignableClinicians]
  );

  // Names that appear more than once in a list, for the email suffix that
  // tells the two people apart. Visit-only pickers count clinicians alone, so a
  // nurse is not marked a duplicate of an office scheduler they never appear
  // beside.
  const duplicateClinicianNames = useMemo(
    () => findDuplicateClinicianNames(clinicians),
    [clinicians]
  );
  const duplicateAssignableClinicianNames = useMemo(
    () => findDuplicateClinicianNames(assignableClinicians),
    [assignableClinicians]
  );

  const formatDuplicateClinicianOptionLabel = useCallback(
    (clinician: ClinicianSelectInput, label: string) => {
      if (duplicateClinicianNames.has(label) && clinician.user_email) {
        return `${label} (${clinician.user_email})`;
      }
      return label;
    },
    [duplicateClinicianNames]
  );

  const getTaskAssigneeLabel = useCallback(
    (task: TaskListResponse) => getAssignedClinicianName(task),
    [getAssignedClinicianName]
  );

  // Options for a task row's assignee picker.
  //
  // An imported chart can already name a clinician who has never signed in.
  // Those staff rows are absent from `/user-clinics/available`, so
  // nobody can be *newly* assigned to them — but a row already assigned to one
  // would find no option matching its own value and fall back to the
  // "Unassigned" placeholder, which is a confident false statement about a
  // task that is assigned. So the current assignee is added back, alone: every
  // other unclaimed person stays out of the list.
  const getTaskAssigneeClinicians = useCallback(
    (task: TaskListResponse): ClinicianSelectInput[] => {
      const available = getClinicAssignableClinicians(
        task.agency_clinic_id,
        getAssigneeDisciplineForTask(task),
        task.assigned_clinician_id
      );
      const assignedId = task.assigned_clinician_id;
      if (
        !assignedId ||
        task.assigned_clinician_unclaimed_import !== true ||
        available.some(c => c.id === assignedId)
      ) {
        return available;
      }
      return [
        {
          id: assignedId,
          // The name the row already displays when it is read-only, so the
          // two branches of this cell never disagree. It arrives formatted
          // (credentials appended by the API), which is why it goes in whole
          // rather than being split into first/last for reformatting.
          user_first_name: getTaskAssigneeLabel(task),
          user_last_name: '',
          is_assignable_clinician: true,
        },
        ...available,
      ];
    },
    [getClinicAssignableClinicians, getTaskAssigneeLabel]
  );

  return {
    clinicians,
    assignableClinicians,
    getClinicAssignableClinicians,
    duplicateClinicianNames,
    duplicateAssignableClinicianNames,
    formatDuplicateClinicianOptionLabel,
    getTaskAssigneeLabel,
    getTaskAssigneeClinicians,
  };
}
